import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Badge, Card, Select, Label, ConfirmationDialog } from '../../components/ui';
import { Modal, ModalHeader, ModalTitle, ModalContent, ModalFooter } from '../../components/ui/Modal';
import { useWorkflow, updateWorkflow, testWorkflow, WorkflowRule, TestWorkflowData } from './useData';
import ConditionBuilder from './components/ConditionBuilder';
import ActionSelector from './components/ActionSelector';
import { entityTypes } from './utils/entityFields';
import { useNotification } from '../../contexts/NotificationContext';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';

const WorkflowBuilder: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { colors } = useTheme();
  const { showSuccess, showError } = useNotification();
  const workflowId = id ? parseInt(id, 10) : null;
  const { data: workflow, isLoading, mutate } = useWorkflow(workflowId || 0);
  const [rules, setRules] = useState<WorkflowRule[]>([]);
  const [selectedRuleIndex, setSelectedRuleIndex] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [testResults, setTestResults] = useState<any>(null);
  const [showTestResults, setShowTestResults] = useState(false);
  const [ruleToRemoveIndex, setRuleToRemoveIndex] = useState<number | null>(null);

  useEffect(() => {
    if (workflow) {
      setRules(workflow.rules || []);
      if (workflow.rules && workflow.rules.length > 0) {
        setSelectedRuleIndex(0);
      }
    }
  }, [workflow]);

  const handleAddRule = () => {
    const newRule: WorkflowRule = {
      trigger: 'entity',
      entityType: 'Task',
      conditions: [],
      actions: [],
      order: rules.length,
    };
    const updatedRules = [...rules, newRule];
    setRules(updatedRules);
    setSelectedRuleIndex(updatedRules.length - 1);
  };

  const handleUpdateRule = (index: number, updates: Partial<WorkflowRule>) => {
    const updated = [...rules];
    updated[index] = { ...updated[index], ...updates };
    setRules(updated);
  };

  const handleRemoveRule = (index: number) => {
    setRuleToRemoveIndex(index);
  };

  const confirmRemoveRule = () => {
    if (ruleToRemoveIndex === null) return;
    const index = ruleToRemoveIndex;
    setRuleToRemoveIndex(null);
    const updated = rules.filter((_, i) => i !== index);
    setRules(updated);
    if (selectedRuleIndex === index) {
      setSelectedRuleIndex(updated.length > 0 ? 0 : null);
    } else if (selectedRuleIndex !== null && selectedRuleIndex > index) {
      setSelectedRuleIndex(selectedRuleIndex - 1);
    }
  };

  const handleSave = async () => {
    if (!workflow) return;

    setIsSaving(true);
    try {
      await updateWorkflow(workflow.id, {
        ...workflow,
        rules: rules.map((rule, index) => ({
          ...rule,
          order: index,
        })),
      });
      await mutate();
      showSuccess('Workflow saved successfully');
    } catch (error: any) {
      showError(error.message || 'Failed to save workflow');
    } finally {
      setIsSaving(false);
    }
  };

  const handleTest = async () => {
    if (!workflow || selectedRuleIndex === null) {
      showError('Please select a rule to test');
      return;
    }

    const rule = rules[selectedRuleIndex];
    if (!rule.conditions || (Array.isArray(rule.conditions) ? rule.conditions.length === 0 : !rule.conditions.conditions || rule.conditions.conditions.length === 0)) {
      showError('Please add at least one condition');
      return;
    }
    if (!rule.actions || (Array.isArray(rule.actions) ? rule.actions.length === 0 : false)) {
      showError('Please add at least one action');
      return;
    }

    setIsTesting(true);
    try {
      const testData: TestWorkflowData = {
        entityType: (rule as any).entityType || 'Task',
        entityId: 1,
        trigger: rule.trigger,
        entityData: {
          status: 'Open',
          priority: 'High',
        },
      };

      const result = await testWorkflow(workflow.id, testData);
      setTestResults(result);
      setShowTestResults(true);
      showSuccess('Workflow test completed successfully!');
    } catch (error: any) {
      showError(error.message || 'Failed to test workflow');
    } finally {
      setIsTesting(false);
    }
  };

  if (isLoading) {
    return (
      <div className={cn('flex items-center justify-center min-h-screen', colors.background)}>
        <LoadingSpinner size="large" message="Loading workflow..." />
      </div>
    );
  }

  if (!workflow) {
    return (
      <div className={cn('flex items-center justify-center min-h-screen', colors.background)}>
        <Card className="p-8 text-center max-w-md">
          <h2 className={cn('text-2xl font-bold mb-2', colors.text)}>Workflow not found</h2>
          <p className={cn('mb-6', colors.textMuted)}>The workflow you are looking for does not exist.</p>
          <Button onClick={() => navigate('/workflows')}>Back to Workflows</Button>
        </Card>
      </div>
    );
  }

  const selectedRule = selectedRuleIndex !== null ? rules[selectedRuleIndex] : null;

  return (
    <div className={cn('h-full flex flex-col overflow-hidden min-h-0', colors.background)}>
      {/* Top Header Bar */}
      <div className={cn('border-b shadow-sm flex-shrink-0 z-30', colors.surface, colors.border)}>
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/workflows')}
                className={cn('p-2 rounded-lg transition-colors', colors.interactiveHover)}
                title="Back to workflows"
              >
                <svg className={cn('w-5 h-5', colors.textMuted)} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
              <div>
                <h1 className={cn('text-2xl font-bold', colors.text)}>{workflow.name}</h1>
                {workflow.description && (
                  <p className={cn('text-sm mt-1', colors.textMuted)}>{workflow.description}</p>
                )}
              </div>
              <Badge variant={workflow.isActive ? 'accent' : 'secondary'}>
                {workflow.isActive ? 'Active' : 'Inactive'}
              </Badge>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex overflow-hidden min-h-0">
        <>
            {/* Rules Sidebar */}
            <div className={cn(`${sidebarOpen ? 'w-80' : 'w-0'} border-r flex flex-col transition-all duration-300 overflow-hidden`, colors.surface, colors.border)}>
              {sidebarOpen && (
                <div className="flex flex-col h-full">
                  <div className={cn('p-4 border-b', colors.border)}>
                    <h2 className={cn('text-lg font-semibold', colors.text)}>Workflow Rules</h2>
                  </div>
                  <div className="flex-1 overflow-y-auto p-4">
                    <Button
                      variant="default"
                      className="w-full mb-4"
                      onClick={handleAddRule}
                    >
                      <span className="flex items-center gap-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add New Rule
                      </span>
                    </Button>
                    <div className="space-y-3">
                      {rules.length === 0 ? (
                        <div className={cn('text-center py-8', colors.textMuted)}>
                          <svg className={cn('w-12 h-12 mx-auto mb-3', colors.textMuted)} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <p className="text-sm">No rules yet</p>
                          <p className="text-xs mt-1">Click "Add New Rule" to get started</p>
                        </div>
                      ) : (
                        rules.map((rule, index) => {
                          const conditions = Array.isArray(rule.conditions) 
                            ? rule.conditions 
                            : (rule.conditions?.conditions || []);
                          const actions = Array.isArray(rule.actions) ? rule.actions : [];
                          const isSelected = selectedRuleIndex === index;
                          
                          return (
                            <Card
                              key={index}
                              className={cn(
                                'p-4 cursor-pointer transition-all',
                                isSelected ? cn('border-2 shadow-md', colors.primaryBorderAll, colors.surfaceHover) : cn('border hover:shadow-sm', colors.border)
                              )}
                              onClick={() => setSelectedRuleIndex(index)}
                            >
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <div className={cn(
                                    'w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold',
                                    isSelected ? cn(colors.primary, colors.primaryText) : cn(colors.surfaceHover, colors.textSecondary)
                                  )}>
                                    {index + 1}
                                  </div>
                                  <div>
                                    <div className={cn('font-semibold text-sm', colors.text)}>Rule {index + 1}</div>
                                    <div className={cn('text-xs mt-0.5', colors.textMuted)}>
                                      {(rule as any).entityType || 'All Entities'} • {rule.trigger}
                                    </div>
                                  </div>
                                </div>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveRule(index);
                                  }}
                                  className={cn('p-1 rounded transition-colors', colors.error)}
                                  title="Remove rule"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              </div>
                              <div className="mt-3 space-y-2">
                                <div className="flex items-center gap-2 text-xs">
                                  <span className={cn('px-2 py-1 rounded', colors.warning)}>
                                    {conditions.length} condition{conditions.length !== 1 ? 's' : ''}
                                  </span>
                                  <span className={cn('px-2 py-1 rounded', colors.success)}>
                                    {actions.length} action{actions.length !== 1 ? 's' : ''}
                                  </span>
                                </div>
                              </div>
                            </Card>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>
              )}
              {!sidebarOpen && (
                <button
                  onClick={() => setSidebarOpen(true)}
                  className={cn('absolute left-0 top-1/2 -translate-y-1/2 border rounded-r-lg p-2 shadow-sm transition-colors z-10', colors.surface, colors.border, colors.interactiveHover)}
                  title="Expand sidebar"
                >
                  <svg className={cn('w-5 h-5', colors.textMuted)} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              )}
            </div>

            {/* Main Editor Area */}
            <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
              {/* Scrollable Content */}
              <div className={cn('flex-1 overflow-y-auto min-h-0', colors.background)} style={{ maxHeight: 'calc(100% - 80px)', height: 'calc(100% - 80px)' }}>
                {selectedRule ? (
                  <div className="max-w-4xl mx-auto p-6 pb-6">
                    <Card className="p-6 mb-6">
                      <div className="flex items-center justify-between mb-6">
                        <div>
                          <h2 className={cn('text-xl font-bold', colors.text)}>Edit Rule {selectedRuleIndex! + 1}</h2>
                          <p className={cn('text-sm mt-1', colors.textMuted)}>Configure when and how this rule should execute</p>
                        </div>
                      </div>

                      <div className="space-y-6">
                        {/* Entity Type Selection */}
                        <div>
                          <Label className={cn('block mb-2', colors.textSecondary)}>
                            Entity Type
                          </Label>
                          <Select
                            value={(selectedRule as any).entityType || 'Task'}
                            onChange={(e) => {
                              const entityType = e.target.value;
                              handleUpdateRule(selectedRuleIndex!, {
                                entityType,
                                trigger: entityType === 'Email' ? 'email_received' : (selectedRule.trigger === 'email_received' ? 'entity' : selectedRule.trigger),
                                conditions: [],
                                actions: [],
                              });
                            }}
                            options={entityTypes.map((t) => ({ value: t.value, label: t.label }))}
                            placeholder="Select entity type..."
                            className="w-full"
                          />
                          <p className={cn('mt-2 text-xs', colors.textMuted)}>
                            Select which entity type this rule applies to. Leave as "Task" for task-specific rules.
                          </p>
                        </div>

                        {/* Trigger Type */}
                        <div>
                          <Label className={cn('block mb-2', colors.textSecondary)}>
                            Trigger Type
                          </Label>
                          <Select
                            value={selectedRule.trigger}
                            onChange={(e) =>
                              handleUpdateRule(selectedRuleIndex!, { trigger: e.target.value })
                            }
                            options={[
                              { value: 'entity', label: 'Entity Event (on create/update)' },
                              { value: 'email_received', label: 'Email Received' },
                              { value: 'field_change', label: 'Field Change' },
                              { value: 'schedule', label: 'Scheduled' },
                            ]}
                            placeholder="Select trigger..."
                            className="w-full"
                          />
                          <p className={cn('mt-2 text-xs', colors.textMuted)}>
                            Choose when this workflow rule should be triggered.
                          </p>
                        </div>

                        {/* Conditions Section */}
                        <div className={cn('border-t pt-6', colors.border)}>
                          <div className="flex items-center gap-3 mb-4">
                            <div className={cn('w-10 h-10 rounded-full flex items-center justify-center', colors.warning)}>
                              <svg className={cn('w-5 h-5', colors.text)} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </div>
                            <div>
                              <h3 className={cn('text-lg font-semibold', colors.text)}>Conditions</h3>
                              <p className={cn('text-sm', colors.textMuted)}>Define when this rule should execute</p>
                            </div>
                          </div>
                          <ConditionBuilder
                            conditions={Array.isArray(selectedRule.conditions) ? selectedRule.conditions : []}
                            onChange={(conditions) =>
                              handleUpdateRule(selectedRuleIndex!, { conditions })
                            }
                            entityType={(selectedRule as any).entityType || 'Task'}
                          />
                        </div>

                        {/* Actions Section */}
                        <div className={cn('border-t pt-6', colors.border)}>
                          <div className="flex items-center gap-3 mb-4">
                            <div className={cn('w-10 h-10 rounded-full flex items-center justify-center', colors.success)}>
                              <svg className={cn('w-5 h-5', colors.text)} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                              </svg>
                            </div>
                            <div>
                              <h3 className={cn('text-lg font-semibold', colors.text)}>Actions</h3>
                              <p className={cn('text-sm', colors.textMuted)}>What should happen when conditions are met</p>
                            </div>
                          </div>
                          <ActionSelector
                            actions={Array.isArray(selectedRule.actions) ? selectedRule.actions : []}
                            onChange={(actions) =>
                              handleUpdateRule(selectedRuleIndex!, { actions })
                            }
                            entityType={(selectedRule as any).entityType || 'Task'}
                          />
                        </div>
                      </div>
                    </Card>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full min-h-[400px]">
                    <div className="text-center max-w-md">
                      <svg className={cn('w-16 h-16 mx-auto mb-4', colors.textMuted)} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <h3 className={cn('text-xl font-semibold mb-2', colors.text)}>No Rule Selected</h3>
                      <p className={cn('mb-6', colors.textMuted)}>
                        {rules.length === 0
                          ? 'Create your first workflow rule to get started'
                          : 'Select a rule from the sidebar to edit, or create a new one'}
                      </p>
                      {rules.length === 0 && (
                        <Button variant="default" onClick={handleAddRule}>
                          Create First Rule
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Fixed Action Bar - Always visible */}
              <div className={cn('flex-shrink-0 border-t shadow-lg px-6 py-4 z-10 h-20', colors.surface, colors.border)}>
                <div className="max-w-4xl mx-auto flex items-center justify-between">
                  <div className={cn('flex items-center gap-2 text-sm', colors.textMuted)}>
                    <span className="font-medium">{rules.length}</span>
                    <span>rule{rules.length !== 1 ? 's' : ''} configured</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Button 
                      variant="outline" 
                      onClick={() => navigate('/workflows')}
                      className="min-w-[100px]"
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleTest}
                      disabled={isTesting || selectedRuleIndex === null}
                      className="min-w-[120px]"
                    >
                      {isTesting ? (
                        <span className="flex items-center gap-2">
                          <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                          Testing...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Test Rule
                        </span>
                      )}
                    </Button>
                    <Button 
                      variant="default" 
                      onClick={handleSave} 
                      disabled={isSaving}
                      className="min-w-[140px]"
                    >
                      {isSaving ? (
                        <span className="flex items-center gap-2">
                          <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                          Saving...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          Save Workflow
                        </span>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
        </>
      </div>

      {/* Test Results Modal */}
      <Modal isOpen={showTestResults} onClose={() => setShowTestResults(false)} size="md">
        <ModalHeader>
          <ModalTitle>Workflow Test Results</ModalTitle>
          <button
            onClick={() => setShowTestResults(false)}
            className={cn('p-2 rounded-lg transition-colors', colors.interactiveHover)}
            title="Close"
          >
            <svg className={cn('w-5 h-5', colors.textMuted)} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </ModalHeader>
        <div className="max-h-[60vh] overflow-y-auto">
          <ModalContent>
            {testResults ? (
              <div className="space-y-4">
              <div>
                <h3 className={cn('text-sm font-semibold mb-2', colors.textSecondary)}>Test Summary</h3>
                <div className={cn('rounded-lg p-4 space-y-2', colors.surfaceHover)}>
                  <div className="flex items-center justify-between">
                    <span className={cn('text-sm', colors.textMuted)}>Status:</span>
                    <Badge variant={testResults.success ? 'accent' : 'danger'}>
                      {testResults.success ? 'Success' : 'Failed'}
                    </Badge>
                  </div>
                  {testResults.matched && (
                    <div className="flex items-center justify-between">
                      <span className={cn('text-sm', colors.textMuted)}>Rules Matched:</span>
                      <span className={cn('text-sm font-medium', colors.text)}>{testResults.matched.length}</span>
                    </div>
                  )}
                  {testResults.executed && (
                    <div className="flex items-center justify-between">
                      <span className={cn('text-sm', colors.textMuted)}>Actions Executed:</span>
                      <span className={cn('text-sm font-medium', colors.text)}>{testResults.executed.length}</span>
                    </div>
                  )}
                </div>
              </div>

              {testResults.matched && testResults.matched.length > 0 && (
                <div>
                  <h3 className={cn('text-sm font-semibold mb-2', colors.textSecondary)}>Matched Rules</h3>
                  <div className="space-y-2">
                    {testResults.matched.map((rule: any, index: number) => (
                      <Card key={index} className="p-3">
                        <div className="text-sm">
                          <div className={cn('font-medium', colors.text)}>Rule {index + 1}</div>
                          <div className={cn('text-xs mt-1', colors.textMuted)}>
                            Trigger: {rule.trigger} • Entity: {rule.entityType || 'All'}
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {testResults.executed && testResults.executed.length > 0 && (
                <div>
                  <h3 className={cn('text-sm font-semibold mb-2', colors.textSecondary)}>Executed Actions</h3>
                  <div className="space-y-2">
                    {testResults.executed.map((action: any, index: number) => (
                      <Card key={index} className="p-3">
                        <div className="text-sm">
                          <div className={cn('font-medium', colors.text)}>{action.type}</div>
                          {action.message && (
                            <div className={cn('text-xs mt-1', colors.textMuted)}>{action.message}</div>
                          )}
                          {action.success !== undefined && (
                            <div className="mt-1">
                              <Badge variant={action.success ? 'accent' : 'danger'}>
                                {action.success ? 'Success' : 'Failed'}
                              </Badge>
                            </div>
                          )}
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {testResults.error && (
                <div>
                  <h3 className={cn('text-sm font-semibold mb-2', colors.error)}>Error</h3>
                  <div className={cn('border rounded-lg p-3', colors.error)}>
                    <p className={cn('text-sm', colors.text)}>{testResults.error}</p>
                  </div>
                </div>
              )}

              <div>
                <h3 className={cn('text-sm font-semibold mb-2', colors.textSecondary)}>Raw Results</h3>
                <div className={cn('rounded-lg p-4 overflow-x-auto', colors.surface)}>
                  <pre className={cn('text-xs whitespace-pre-wrap', colors.text)}>
                    {JSON.stringify(testResults, null, 2)}
                  </pre>
                </div>
              </div>
              </div>
            ) : (
              <div className={cn('text-center py-8', colors.textMuted)}>
                <p>No test results available</p>
              </div>
            )}
          </ModalContent>
        </div>
        <ModalFooter>
          <Button variant="outline" onClick={() => setShowTestResults(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      <ConfirmationDialog
        isOpen={ruleToRemoveIndex !== null}
        onClose={() => setRuleToRemoveIndex(null)}
        onConfirm={confirmRemoveRule}
        title="Remove rule"
        message="Are you sure you want to remove this rule?"
        confirmText="Remove"
        cancelText="Cancel"
        confirmVariant="danger"
      />
    </div>
  );
};

export default WorkflowBuilder;
